import React, { useEffect, useState, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  FeatureGroup,
  ImageOverlay,
} from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import { useDispatch, useSelector } from "react-redux";
import {
  addArea,
  clearAllMapState,
  deleteArea,
  setFileContent,
  setUploaderFlag,
  setShowOverlay,
} from "../../reducers/MapSlice";
import { useMap } from "react-leaflet/hooks";
import L from "leaflet";
import LZString from "lz-string";
import { useTranslation } from "react-i18next";
import { url } from "../../api/axios_client";
import { toast } from "react-toastify";
import {
  setFeaturesColumn,
  setLoadingfeatures,
} from "../../reducers/PredictionSlice";
import axios from "../../api/axios_client";
import WorkingAreaBoundary from "../../components/WorkingAreaBoundary";

// Component to save and restore map zoom/center
function MapStatePersistence() {
  const map = useMap();

  useEffect(() => {
    // Restore zoom and center from localStorage
    const savedZoom = localStorage.getItem('mapZoom');
    const savedCenter = localStorage.getItem('mapCenter');

    if (savedZoom && savedCenter) {
      try {
        const zoom = parseInt(savedZoom, 10);
        const center = JSON.parse(savedCenter);
        map.setView(center, zoom);
      } catch (error) {
        console.error('Error restoring map state:', error);
      }
    }

    // Save zoom and center on changes
    const handleMoveEnd = () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      localStorage.setItem('mapZoom', zoom.toString());
      localStorage.setItem('mapCenter', JSON.stringify([center.lat, center.lng]));
    };

    map.on('moveend', handleMoveEnd);
    map.on('zoomend', handleMoveEnd);

    return () => {
      map.off('moveend', handleMoveEnd);
      map.off('zoomend', handleMoveEnd);
    };
  }, [map]);

  return null;
}

function useShapeClearing() {
  const mapRef = useRef();

  const clearAllShapes = () => {
    if (mapRef.current) {
      const map = mapRef.current;
      map.eachLayer((layer) => {
        if (
          layer instanceof L.Polygon ||
          layer instanceof L.GeoJSON ||
          layer instanceof L.Circle ||
          layer instanceof L.Rectangle
        ) {
          map.removeLayer(layer);
        }
      });
    }
  };

  useEffect(() => {
    const handleClearShapes = () => {
      clearAllShapes();
    };

    window.addEventListener('clearDrawnShapes', handleClearShapes);

    return () => {
      window.removeEventListener('clearDrawnShapes', handleClearShapes);
    };
  }, []);

  return { mapRef, clearAllShapes };
}

function MapInstanceRegistrar({ onMapReady }) {
  const map = useMap();

  useEffect(() => {
    if (map && onMapReady) {
      onMapReady(map);
    }
  }, [map, onMapReady]);

  return null;
}

// Component to update overlay style
function UpdateOverlayStyle({ geoJsonLayerRef, showOverlay }) {
  const map = useMap();

  useEffect(() => {
    if (geoJsonLayerRef) {
      geoJsonLayerRef.eachLayer((layer) => {
        layer.setStyle({
          fillOpacity: showOverlay ? 0.2 : 0,
          fillColor: '#1976d2',
          weight: 2,
          color: '#1976d2'
        });
      });
    }
  }, [showOverlay, geoJsonLayerRef]);

  return null;
}

// Component to add GeoJSON and fetch feature attributes
function AddGeoJsonComponent({ fileContent, geoJsonLayerRef, setGeoJsonLayerRef, showOverlay }) {
  const map = useMap();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!fileContent || geoJsonLayerRef) return;

    const addGeoJson = async () => {
      try {
        dispatch(setLoadingfeatures(true));

        // Clear existing GeoJSON layers (but not working area boundary)
        map.eachLayer((layer) => {
          if (layer instanceof L.GeoJSON &&
            !layer.options.className?.includes('working-area-boundary')) {
            map.removeLayer(layer);
          }
        });

        // Validate fileContent
        if (!fileContent || typeof fileContent !== "object") {
          throw new Error("Invalid fileContent");
        }

        // Create GeoJSON layer
        const geoJsonLayer = L.geoJSON(fileContent, {
          style: {
            fillOpacity: showOverlay ? 0.2 : 0,
            fillColor: '#1976d2',
            weight: 2,
            color: '#1976d2'
          },
          onEachFeature: (feature, layer) => {
            if (feature.properties) {
              layer.bindPopup(
                Object.entries(feature.properties)
                  .map(([key, value]) => `${key}: ${value}`)
                  .join('<br>')
              );
            }
          }
        });

        if (!geoJsonLayer) {
          throw new Error('Failed to create GeoJSON layer');
        }

        geoJsonLayer.addTo(map);
        const bounds = geoJsonLayer.getBounds();

        if (!bounds.isValid()) {
          throw new Error('Invalid bounds');
        }

        setGeoJsonLayerRef(geoJsonLayer);

        dispatch(
          addArea({
            polyId: geoJsonLayer._leaflet_id,
            coordinates: fileContent,
          })
        );

        // Fetch feature validation for attributes column
        const res = await axios.post(
          `${url}/prediction/feature-validation`,
          { geo: JSON.stringify(fileContent) },
          { headers: { "Content-Type": "application/json" } }
        );

        if (res.data && res.data.data) {
          dispatch(setFeaturesColumn(res.data.data));
        }

        map.fitBounds(bounds);
      } catch (error) {
        console.error('Error in AddGeoJson:', error);
        toast.error('Failed to display GeoJSON on map');
        dispatch(setFileContent(""));
      } finally {
        dispatch(setLoadingfeatures(false));
      }
    };

    addGeoJson();
  }, [fileContent, geoJsonLayerRef, map, dispatch, setGeoJsonLayerRef, showOverlay]);

  return null;
}

// FIX: Component to restore drawn shapes from Redux - runs only once on mount
function RestoreDrawnShapes({ areas }) {
  const map = useMap();
  const showOverlay = useSelector((state) => state.mapState.showOverlay);

  // Run only once on mount - empty dependency array
  useEffect(() => {
    if (!areas || areas.length === 0) return;

    areas.forEach((area) => {
      // Skip uploaded GeoJSON files
      if (typeof area.coordinates === 'object' && area.coordinates.type === 'FeatureCollection') {
        return;
      }

      // Skip invalid coordinates
      if (!Array.isArray(area.coordinates) || area.coordinates.length === 0) {
        return;
      }

      try {
        L.polygon(
          area.coordinates.map(coord => [coord[1], coord[0]]),
          {
            fillOpacity: showOverlay ? 0.2 : 0,
            fillColor: '#1976d2',
            weight: 2,
            color: '#1976d2'
          }
        ).addTo(map);
      } catch (error) {
        console.error('Error restoring shape:', error);
      }
    });
  }, []); // Empty dependency array - only run once on mount

  return null;
}

function Map(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const center = { lat: 19.55979, lng: 29.267578 };

  const fileContent = useSelector((state) => state.mapState.uploadedFileContent);
  const uploaderFlag = useSelector((state) => state.mapState.uploaderFlag);
  const showOverlay = useSelector((state) => state.mapState.showOverlay);
  const [deleteState, setDelete] = useState(false);
  const [geoJsonLayerRef, setGeoJsonLayerRef] = useState(null);
  const { mapRef, clearAllShapes } = useShapeClearing();
  const areas = useSelector((state) => state.mapState.areas);

  // Track previous fileContent to detect new uploads
  const prevFileContentRef = useRef(null);

  useEffect(() => {
    const retrievedCompressed = localStorage.getItem("mapFileContent") ?? "";
    const savedMapContent = LZString.decompress(retrievedCompressed);

    if (savedMapContent) {
      try {
        const parsedContent = JSON.parse(savedMapContent);
        dispatch(setFileContent(parsedContent));
        dispatch(setUploaderFlag(true));
      } catch (error) {
        console.error('Error restoring map content:', error);
      }
    }
  }, [dispatch]);

  // FIX: Reset geoJsonLayerRef when fileContent changes (new upload)
  useEffect(() => {
    // Only act on valid GeoJSON objects
    if (fileContent && typeof fileContent === 'object' && fileContent.type === 'FeatureCollection') {
      // Check if this is different from previous content
      if (prevFileContentRef.current !== null) {
        const prevStr = JSON.stringify(prevFileContentRef.current);
        const currStr = JSON.stringify(fileContent);
        if (prevStr !== currStr) {
          // New content - reset geoJsonLayerRef to trigger AddGeoJsonComponent
          setGeoJsonLayerRef(null);
        }
      }
      prevFileContentRef.current = fileContent;
    } else if (!fileContent || fileContent === "") {
      prevFileContentRef.current = null;
    }
  }, [fileContent]);

  const _onCreated = (e) => {
    let layer = e.layer;
    let coordinates;

    // Handle different shape types
    if (layer instanceof L.Circle) {
      // For circles, we need to convert to a polygon with proper lat/lng coordinates
      const center = layer.getLatLng();
      const radius = layer.getRadius(); // in meters
      const points = 64; // number of points to approximate circle

      coordinates = [];
      for (let i = 0; i <= points; i++) {
        const angle = (i / points) * 2 * Math.PI;
        // Calculate offset in meters
        const dx = radius * Math.cos(angle);
        const dy = radius * Math.sin(angle);

        // Convert meters to degrees (approximate)
        // 1 degree latitude ≈ 111,320 meters
        // 1 degree longitude ≈ 111,320 * cos(latitude) meters
        const latOffset = dy / 111320;
        const lngOffset = dx / (111320 * Math.cos(center.lat * Math.PI / 180));

        const lat = center.lat + latOffset;
        const lng = center.lng + lngOffset;

        coordinates.push([lng, lat]); // GeoJSON format: [lng, lat]
      }
    } else {
      // For rectangles, polygons, and other shapes
      const geoJson = layer.toGeoJSON();
      coordinates = geoJson.geometry.coordinates[0];
    }

    dispatch(
      addArea({
        polyId: layer._leaflet_id,
        coordinates: coordinates,
      })
    );
  };

  const _onDeleted = (e) => {
    const deletedKeys = Object.keys(e.layers._layers);
    dispatch(deleteArea(deletedKeys));
  };

  const handleMapReady = (map) => {
    mapRef.current = map;
  };

  function DeleteGeoJson() {
    const map = useMap();
    map.eachLayer(function (layer) {
      // Skip working area boundary layer when clearing
      if ((layer instanceof L.Polygon || layer instanceof L.GeoJSON) &&
        !layer.options.className?.includes('working-area-boundary')) {
        layer.remove();
      }
    });
    localStorage.removeItem("mapFileContent");
    dispatch(setUploaderFlag(false));
    setDelete(false);
    dispatch(clearAllMapState());
    setGeoJsonLayerRef(null);
  }

  const deleteUploadedFiles = () => {
    localStorage.removeItem("mapFileContent");
    dispatch(setFileContent(""));
    dispatch(setUploaderFlag(false));
    setDelete(true);
    setGeoJsonLayerRef(null);
  };

  const ArabWorldBounds = {
    southWest: {
      lat: -18.454445427710418,
      lng: -18.675611088484246,
    },
    northEast: {
      lat: 45.261019159784439,
      lng: 60.756334763925977,
    },
  };

  const bounds = L.latLngBounds(
    ArabWorldBounds.southWest,
    ArabWorldBounds.northEast
  );

  return (
    <>
      <MapContainer
        bounds={bounds}
        maxBounds={bounds}
        center={center}
        minZoom={4}
      >
        <MapStatePersistence />
        <MapInstanceRegistrar onMapReady={handleMapReady} />
        <RestoreDrawnShapes areas={areas} />
        <WorkingAreaBoundary />

        {/* Render images for all areas that have images */}
        {areas &&
          areas.map(
            (area, index) => {
              if ((area.imgurl || area.img64) && area.bbox) {
                return (
                  <ImageOverlay
                    key={`image-${index}-${area.polyId}`}
                    url={
                      area.imgurl ||
                      (area.img64 && `data:image/png;base64,${area.img64}`)
                    }
                    bounds={area.bbox}
                  />
                );
              }
              return null;
            }
          )}

        {fileContent && uploaderFlag && (
          <AddGeoJsonComponent
            fileContent={fileContent}
            geoJsonLayerRef={geoJsonLayerRef}
            setGeoJsonLayerRef={setGeoJsonLayerRef}
            showOverlay={showOverlay}
          />
        )}
        {geoJsonLayerRef && <UpdateOverlayStyle geoJsonLayerRef={geoJsonLayerRef} showOverlay={showOverlay} />}
        {deleteState && <DeleteGeoJson />}

        <TileLayer
          attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
          url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
        />

        <FeatureGroup>
          <EditControl
            position="topleft"
            onCreated={_onCreated}
            onDeleted={_onDeleted}
            onDrawStart={(e) => {
              localStorage.removeItem("mapFileContent");
              dispatch(setFileContent(""));
              dispatch(setUploaderFlag(false));
              setDelete(true);
              setGeoJsonLayerRef(null);

              const map = e.target;
              map.eachLayer((layer) => {
                // Skip working area boundary layer when clearing
                if ((layer instanceof L.Polygon || layer instanceof L.GeoJSON) &&
                  !layer.options.className?.includes('working-area-boundary')) {
                  map.removeLayer(layer);
                }
              });
            }}
            draw={{
              rectangle: true,
              circle: true,
              polyline: false,
              marker: false,
              polygon: true,
              circlemarker: false,
            }}
          />
        </FeatureGroup>
      </MapContainer>

      {uploaderFlag && (
        <div className="remove-area" style={{ display: 'flex', gap: '10px' }}>
          <button className="delete-button" onClick={() => deleteUploadedFiles()}>
            {t("Map.remove_file")}
          </button>
          {geoJsonLayerRef && (
            <button
              className="delete-button"
              onClick={() => dispatch(setShowOverlay(!showOverlay))}
              style={{
                backgroundColor: showOverlay ? '#329ace' : '#666',
              }}
            >
              {showOverlay ? t("Map.hide_overlay") || "Hide Overlay" : t("Map.show_overlay") || "Show Overlay"}
            </button>
          )}
        </div>
      )}
    </>
  );
}
export default Map;